/**
 * LOCATION: dashboard-business/src/pages/Analytics.js
 * Mobile responsive — FA icons only, no emojis
 */

import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import AuthService from '../services/auth';
import API from '../services/api';

const loadFA = () => {
  if (!document.getElementById('fa-cdn-analytics')) {
    const fa = document.createElement('link');
    fa.id = 'fa-cdn-analytics'; fa.rel = 'stylesheet';
    fa.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css';
    document.head.appendChild(fa);
  }
};

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const Analytics = () => {
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [period,  setPeriod]  = useState('7d');
  const user = AuthService.getCurrentUser();
  const plan = user?.plan || 'basic';
  const isBasic = plan === 'basic' || plan === 'free';

  useEffect(() => {
    loadFA();
    loadStats();
  }, [period]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const res = await API.getStats();
      if (res.success) setStats(res.stats);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // Fake chart data derived from real stats (illustrative)
  const totalChats    = stats?.totalChats    || 0;
  const activeChats   = stats?.activeChats   || 0;
  const totalAgents   = stats?.totalAgents   || 0;
  const messagesUsed  = stats?.messagesUsed  || 0;
  const messageLimit  = stats?.messageLimit  || 50;
  const msgPct        = Math.min(Math.round((messagesUsed / messageLimit) * 100), 100);

  // Simulated weekly bar data
  const days = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  const barData = days.map((d, i) => ({
    day:   d,
    value: Math.max(2, Math.round((totalChats / 7) * (0.5 + Math.sin(i * 0.8 + 1) * 0.5))),
  }));
  const maxBar = Math.max(...barData.map(b => b.value), 1);

  const kpis = [
    { icon: 'fa-comments',      color: '#667eea', bg: '#eff0ff', label: 'Total Chats',      value: totalChats,                sub: 'All time conversations'      },
    { icon: 'fa-circle-dot',    color: '#10b981', bg: '#d1fae5', label: 'Active Now',        value: activeChats,               sub: 'Live right now'              },
    { icon: 'fa-comment-dots',  color: '#f59e0b', bg: '#fef3c7', label: 'Messages Used',     value: `${messagesUsed}/${messageLimit}`, sub: `${msgPct}% of limit used` },
    { icon: 'fa-users',         color: '#8b5cf6', bg: '#ede9fe', label: 'Active Agents',     value: totalAgents,               sub: 'Team members'                },
    { icon: 'fa-star',          color: '#ec4899', bg: '#fce7f3', label: 'Satisfaction',      value: '—',                       sub: isBasic ? 'Pro feature' : 'No data yet' },
    { icon: 'fa-clock',         color: '#06b6d4', bg: '#cffafe', label: 'Avg. Response',     value: '—',                       sub: isBasic ? 'Pro feature' : 'No data yet' },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        .an-layout{display:flex;min-height:100vh;font-family:'DM Sans',sans-serif;}
        .an-main{flex:1;padding:32px;background:#f8fafc;overflow-y:auto;min-width:0;}
        .an-header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:28px;flex-wrap:wrap;gap:12px;}
        .an-title{font-size:28px;font-weight:800;color:#111827;margin:0 0 4px;display:flex;align-items:center;gap:10px;}
        .an-sub{font-size:14px;color:#6b7280;margin:0;}
        .an-period{display:inline-flex;background:#fff;border:1px solid #e5e7eb;border-radius:12px;padding:4px;gap:3px;box-shadow:0 1px 3px rgba(0,0,0,0.05);}
        .an-pbtn{padding:7px 14px;border:none;border-radius:9px;font-size:13px;font-weight:600;cursor:pointer;background:transparent;color:#6b7280;font-family:'DM Sans',sans-serif;transition:all 0.2s;}
        .an-pbtn.on{background:#667eea;color:#fff;box-shadow:0 2px 6px rgba(102,126,234,0.3);}
        .an-kpis{display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:14px;margin-bottom:24px;}
        .an-kpi{background:#fff;border-radius:14px;border:1px solid #e5e7eb;padding:18px 20px;box-shadow:0 1px 4px rgba(0,0,0,0.04);position:relative;overflow:hidden;}
        .an-kpi-icon{width:40px;height:40px;border-radius:12px;display:flex;align-items:center;justify-content:center;margin-bottom:14px;}
        .an-kpi-val{font-size:26px;font-weight:900;color:#111827;line-height:1;margin-bottom:4px;}
        .an-kpi-lbl{font-size:13px;font-weight:700;color:#374151;margin-bottom:3px;}
        .an-kpi-sub{font-size:11px;color:#9ca3af;}
        .an-kpi-accent{position:absolute;bottom:0;left:0;right:0;height:3px;border-radius:0 0 14px 14px;}
        .an-row{display:grid;grid-template-columns:2fr 1fr;gap:16px;margin-bottom:24px;}
        .an-card{background:#fff;border-radius:14px;border:1px solid #e5e7eb;padding:22px;box-shadow:0 1px 4px rgba(0,0,0,0.04);}
        .an-card-title{font-size:15px;font-weight:800;color:#111827;margin:0 0 20px;display:flex;align-items:center;gap:8px;}
        .an-bars{display:flex;align-items:flex-end;gap:8px;height:120px;}
        .an-bar-wrap{flex:1;display:flex;flex-direction:column;align-items:center;gap:5px;}
        .an-bar{width:100%;border-radius:4px 4px 0 0;background:linear-gradient(180deg,#667eea,#764ba2);transition:height 0.5s ease;min-height:4px;}
        .an-bar-lbl{font-size:11px;color:#9ca3af;font-weight:600;}
        .an-bar-val{font-size:10px;color:#374151;font-weight:700;}
        .an-stat-row{display:flex;justify-content:space-between;align-items:center;padding:10px 0;border-bottom:1px solid #f3f4f6;}
        .an-stat-row:last-child{border-bottom:none;}
        .an-stat-lbl{font-size:13px;color:#6b7280;display:flex;align-items:center;gap:7px;}
        .an-stat-val{font-size:14px;font-weight:800;color:#111827;}
        .an-usage-wrap{margin-bottom:24px;}
        .an-usage-card{background:#fff;border-radius:14px;border:1px solid #e5e7eb;padding:22px;box-shadow:0 1px 4px rgba(0,0,0,0.04);}
        .an-usage-bar-bg{height:10px;background:#f3f4f6;border-radius:100px;overflow:hidden;margin-bottom:8px;}
        .an-usage-bar-fill{height:100%;border-radius:100px;transition:width 0.8s ease;}
        .an-pro-banner{background:linear-gradient(135deg,#667eea,#764ba2);border-radius:14px;padding:20px 24px;display:flex;align-items:center;justify-content:space-between;gap:16px;flex-wrap:wrap;}
        .an-pro-text h3{font-size:16px;font-weight:800;color:#fff;margin:0 0 4px;}
        .an-pro-text p{font-size:13px;color:rgba(255,255,255,0.75);margin:0;}
        .an-pro-btn{padding:10px 20px;background:#fff;color:#667eea;border:none;border-radius:10px;cursor:pointer;font-weight:800;font-size:14px;white-space:nowrap;text-decoration:none;display:inline-flex;align-items:center;gap:7px;}
        @media(max-width:900px){.an-row{grid-template-columns:1fr;}}
        @media(max-width:600px){.an-main{padding:16px;}.an-kpis{grid-template-columns:1fr 1fr;}.an-title{font-size:22px;}.an-bars{height:80px;}}
      `}</style>

      <div className="an-layout">
        <Sidebar />
        <main className="an-main">

          {/* Header */}
          <div className="an-header">
            <div>
              <h1 className="an-title">
                <i className="fa-solid fa-chart-line" style={{ color: '#667eea' }}></i>
                Analytics
              </h1>
              <p className="an-sub">Track your chat performance and usage</p>
            </div>
            <div className="an-period">
              {['7d','30d','90d'].map(p => (
                <button key={p} className={`an-pbtn${period === p ? ' on' : ''}`} onClick={() => setPeriod(p)}>
                  {p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : '90 Days'}
                </button>
              ))}
            </div>
          </div>

          {/* KPI cards */}
          <div className="an-kpis">
            {kpis.map(k => (
              <div key={k.label} className="an-kpi">
                <div className="an-kpi-icon" style={{ background: k.bg }}>
                  <i className={`fa-solid ${k.icon}`} style={{ color: k.color, fontSize: 17 }}></i>
                </div>
                <div className="an-kpi-val">
                  {loading ? <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: 18, color: '#d1d5db' }}></i> : k.value}
                </div>
                <div className="an-kpi-lbl">{k.label}</div>
                <div className="an-kpi-sub">{k.sub}</div>
                <div className="an-kpi-accent" style={{ background: k.color }}></div>
              </div>
            ))}
          </div>

          {/* Message usage bar */}
          <div className="an-usage-wrap">
            <div className="an-usage-card">
              <h3 className="an-card-title">
                <i className="fa-solid fa-gauge-high" style={{ color: '#667eea' }}></i>
                Message Usage This Period
              </h3>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 600 }}>
                  {messagesUsed} of {messageLimit} messages used
                </span>
                <span style={{ fontSize: 13, fontWeight: 800, color: msgPct > 80 ? '#ef4444' : '#10b981' }}>
                  {msgPct}%
                </span>
              </div>
              <div className="an-usage-bar-bg">
                <div
                  className="an-usage-bar-fill"
                  style={{
                    width: `${msgPct}%`,
                    background: msgPct > 80
                      ? 'linear-gradient(90deg,#f59e0b,#ef4444)'
                      : 'linear-gradient(90deg,#667eea,#10b981)',
                  }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                <span style={{ fontSize: 12, color: '#9ca3af' }}>
                  <i className="fa-solid fa-circle-info" style={{ marginRight: 5 }}></i>
                  {messageLimit - messagesUsed} messages remaining
                </span>
                {msgPct > 80 && (
                  <a href="/billing" style={{ fontSize: 12, color: '#667eea', fontWeight: 700, textDecoration: 'none' }}>
                    Upgrade for more <i className="fa-solid fa-arrow-right" style={{ fontSize: 10 }}></i>
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Charts row */}
          <div className="an-row">
            {/* Bar chart */}
            <div className="an-card">
              <h3 className="an-card-title">
                <i className="fa-solid fa-chart-bar" style={{ color: '#667eea' }}></i>
                Chats This Week
              </h3>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9ca3af' }}>
                  <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: 24 }}></i>
                </div>
              ) : (
                <div className="an-bars">
                  {barData.map(b => (
                    <div key={b.day} className="an-bar-wrap">
                      <div className="an-bar-val">{b.value}</div>
                      <div className="an-bar" style={{ height: `${(b.value / maxBar) * 90}px` }} />
                      <div className="an-bar-lbl">{b.day}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quick stats */}
            <div className="an-card">
              <h3 className="an-card-title">
                <i className="fa-solid fa-circle-info" style={{ color: '#667eea' }}></i>
                Quick Stats
              </h3>
              {[
                { icon: 'fa-comments',   color: '#667eea', label: 'Total Chats',    val: loading ? '…' : totalChats    },
                { icon: 'fa-circle-dot', color: '#10b981', label: 'Active Now',     val: loading ? '…' : activeChats   },
                { icon: 'fa-users',      color: '#8b5cf6', label: 'Agents',         val: loading ? '…' : totalAgents   },
                { icon: 'fa-star',       color: '#f59e0b', label: 'Satisfaction',   val: isBasic ? 'Premium' : '—'        },
                { icon: 'fa-clock',      color: '#06b6d4', label: 'Avg. Response',  val: isBasic ? 'Premium' : '—'        },
              ].map(r => (
                <div key={r.label} className="an-stat-row">
                  <span className="an-stat-lbl">
                    <i className={`fa-solid ${r.icon}`} style={{ color: r.color, fontSize: 13, width: 16, textAlign: 'center' }}></i>
                    {r.label}
                  </span>
                  <span className="an-stat-val">{r.val}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Pro upgrade banner (Basic only) */}
          {isBasic && (
            <div className="an-pro-banner">
              <div className="an-pro-text">
                <h3>
                  <i className="fa-solid fa-chart-line" style={{ marginRight: 8 }}></i>
                  Unlock Advanced Analytics
                </h3>
                <p>Get satisfaction ratings, response times, agent performance, and full chat history on Pro or PREMIUM.</p>
              </div>
              <a href="/billing" className="an-pro-btn">
                <i className="fa-solid fa-arrow-up"></i>
                Upgrade Now
              </a>
            </div>
          )}

        </main>
      </div>
    </>
  );
};

export default Analytics;